/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import { PiWarningCircle } from "react-icons/pi";
import { Select, Button } from '@chakra-ui/react';
import DisplayAmount from "../../shared/DisplayAmount";

const PayoutMarkFailedConfirmModal: React.FC<{
    payout: any;
    isOpen: boolean;
    onClose(): void;
    triggerReload(): void;
    setSwalProps: any;
}> = ({ isOpen, onClose, triggerReload, payout, setSwalProps }) => {

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [failureType, setFailureType] = useState<string>("");
    const [reason, setReason] = useState<string>("");
    const userRole = localStorage.getItem("userRole");

    const isFormValid = failureType !== "" && reason !== "" ? true : false;

    const closeModal = async () => {
        setFailureType("");
        setReason("");
        onClose();
    };

    const handleSubmit = async () => {
        try {
            setIsProcessing(true);

            const data = {
                type: failureType,
                reason: reason,
                username: localStorage.getItem("username"),
                userRole: userRole,
                subaccount_id: payout.subaccount.id,
                payoutid: payout.id
            }

            await axios.post(process.env.REACT_APP_API_URL + `/api/${userRole.toLowerCase()}/payout/action/payout-manual-failed`, data)
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Payout has been successfully marked as failed",
                            showConfirmButton: true,
                            didClose: () => {                                
                                triggerReload();
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    } else {
                        setIsProcessing(false);
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response.data.message,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const fieldsRendering = (fields: Record<string, any>) => {
        const renderInput = (fieldName: string, fieldValue: any) => {
            const capitalizeLabel = (label: string) => {
                let modifiedLabel = label
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                switch (label) {
                    case "bsb":
                        modifiedLabel = "BSB";
                        break;
                    case "bic":
                        modifiedLabel = "BIC";
                        break;
                    case "iban":
                        modifiedLabel = "IBAN";
                        break;
                }

                return modifiedLabel;
            };

            return (
                <div className="flex" key={fieldName}>
                    <dt className="w-32 shrink-0 text-gray-700 dark:text-gray-400">
                        {capitalizeLabel(fieldName)}
                    </dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                        {fieldValue}
                    </dd>
                </div>
            );
        };

        return Object.entries(fields).map(([key, value]) => renderInput(key, value));
    };

    return (
        <>
            {payout &&
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-red-50 p-2 text-red-600 ring-1 ring-red-100 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-900/30">
                                    <PiWarningCircle size={20} />
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Mark Payout as Failed — Confirmation
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        This action cannot be undone. Please review the details carefully.
                                    </p>
                                </div>
                            </div>

                            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                                You are about to mark this payout as <span className="font-medium">failed</span>. This action is permanent.
                            </div>

                            <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                                <dl className="flex flex-col gap-3 text-sm">
                                    <div className="flex">
                                        <dt className="w-32 shrink-0 text-gray-700 dark:text-gray-400">
                                            Email
                                        </dt>
                                        <dd className="font-medium text-gray-900 dark:text-white">
                                            {payout.receiver.email}
                                        </dd>
                                    </div>

                                    {fieldsRendering(payout.receiver_account.method_details)}

                                    <div className="flex">
                                        <dt className="w-32 shrink-0 text-gray-700 dark:text-gray-400">
                                            Amount
                                        </dt>
                                        <dd className="font-medium text-gray-900 dark:text-white">
                                            {payout.crypto ? payout.amount : <DisplayAmount amount={Number(payout.amount)} currency={payout.asset} />} {payout.asset}
                                        </dd>
                                    </div>

                                    <div className="flex">
                                        <dt className="w-32 shrink-0 text-gray-700 dark:text-gray-400">
                                            Note
                                        </dt>
                                        <dd className="font-medium text-gray-900 dark:text-white">
                                            {!payout.note || payout.note === "" ? "-" : payout.note}
                                        </dd>
                                    </div>

                                    <div className="flex">
                                        <dt className="w-32 shrink-0 text-gray-700 dark:text-gray-400">
                                            Reference
                                        </dt>
                                        <dd className="font-medium text-gray-900 dark:text-white">
                                            {!payout.reference || payout.reference === "" ? "-" : payout.reference}
                                        </dd>
                                    </div>
                                </dl>
                            </div>

                            <div className="mb-6 space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                                        Failure Type <span className="text-red-500">*</span>
                                    </label>
                                    <Select disabled={isProcessing} id="gender" value={failureType} onChange={(e) => setFailureType(e.target.value)} className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full shadow-sm">
                                        <option value="">-- Please Select --</option>
                                        <option value="returned">Returned</option>
                                        <option value="rejected">Rejected</option>
                                    </Select>
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                                        Reason *
                                    </label>
                                    <textarea
                                        disabled={isProcessing}
                                        className="text-sm shadow-sm border text-primary rounded-md p-2 block w-full bg-white border-gray-300 h-28 resize-none overflow-auto focus:outline-none"
                                        id="note"
                                        autoComplete="off"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="btn btn-default"
                                >
                                    Not now
                                </Button>
                                <Button
                                    onClick={() => handleSubmit()}
                                    isLoading={isProcessing}
                                    isDisabled={!isFormValid || isProcessing}
                                    loadingText='Processing...'
                                    className="btn text-white bg-red-600 hover:bg-red-500">
                                    Mark failed
                                </Button>
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            }
        </>
    );
};

export default PayoutMarkFailedConfirmModal;